import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchResultsDropdown from '../components/SearchResultsDropdown';
import type { ProjectFilters, UniversalSearchItem } from '../services/api';
import { logProjectDetailsPageClicked } from '../services/analytics';
import { parsePeSearchState, type PePendingPreview } from '../utils/searchState';

/**
 * Debounced universal-search dropdown for catalogue pages. Typing (or
 * applying filters) opens `SearchResultsDropdown`; Enter still hands
 * off to the results page via the caller's `onSubmit`.
 *
 * Filters default to the current URL so a carried `projectId` (and
 * region / district / property type) still narrows suggestions even
 * when the page does not pass an explicit filter object.
 */
export function usePeSearchDropdown(filters?: ProjectFilters) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlFilters = useMemo(
    () => parsePeSearchState(location.search).filters,
    [location.search],
  );
  const resolvedFilters = filters ?? urlFilters;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchDismissKey, setSearchDismissKey] = useState(0);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  const handleSelect = useCallback(
    (
      href: string,
      item: UniversalSearchItem | null,
      pendingPreview?: PePendingPreview | null,
    ) => {
      setSearchDismissKey((k) => k + 1);
      setSearch('');
      if (item?.kind === 'project') {
        logProjectDetailsPageClicked(item.id);
      }
      navigate(href, {
        state: {
          pePreviewAt: Date.now(),
          pePendingPreview: pendingPreview ?? null,
        },
      });
    },
    [navigate],
  );

  const dropdownContent = useMemo(
    () =>
      createElement(SearchResultsDropdown, {
        query: debouncedSearch,
        filters: resolvedFilters,
        onSelect: handleSelect,
      }),
    [debouncedSearch, resolvedFilters, handleSelect],
  );

  return {
    search,
    setSearch,
    dismissDropdownKey: searchDismissKey,
    dropdownContent,
  };
}
